import { store } from "@/store";
import { USER_TYPES } from "@/utils/constants";
import { BackendConversation, SocketErrorPayload } from "@/types/chat";
import {
  DEFAULT_SOCKET_ERROR,
  SOCKET_ERROR_MESSAGES,
} from "@/api/chat/constants";

export const getConversationName = (
  conversation: BackendConversation
): string => {
  const userType = store.getState().Auth.userType;

  if (userType === USER_TYPES.HOMEOWNER) {
    return (
      conversation.subjectHomeowner ||
      conversation.subject ||
      [conversation.user.firstName, conversation.user.lastName]
        .filter(Boolean)
        .join(" ")
    );
  } else {
    return (
      conversation.subjectAdmin ||
      conversation.subject ||
      [conversation.user.firstName, conversation.user.lastName]
        .filter(Boolean)
        .join(" ")
    );
  }
};

export const getConversationAvatar = (
  conversation: BackendConversation
): string | undefined => {
  const userType = store.getState().Auth.userType;

  if (userType === USER_TYPES.HOMEOWNER) {
    // Homeowners always see default admin image (no specific image)
    return undefined;
  } else {
    // Admins see the user's profile picture
    return conversation.user.profilePicture;
  }
};

// Turns a structured socket failure into toast copy. Known codes get wording
// written for the user; anything else falls back to the server's own message so
// a newly added error still says something specific.
export const getSocketErrorMessage = (
  error: SocketErrorPayload | undefined
): { title: string; description: string } => {
  const mapped = error?.code ? SOCKET_ERROR_MESSAGES[error.code] : undefined;

  if (mapped) {
    return mapped;
  }

  return error?.message
    ? { title: DEFAULT_SOCKET_ERROR.title, description: error.message }
    : DEFAULT_SOCKET_ERROR;
};

export const getConversationParticipantId = (
  conversation: BackendConversation
): string | null => {
  const userType = store.getState().Auth.userType;
  const currentUserId = store.getState().Auth.user?.id;

  if (
    userType === USER_TYPES.HOMEOWNER &&
    currentUserId === conversation.userId
  ) {
    // Current user is the homeowner in this conversation
    // The other participant is an admin, but we need to find the admin ID
    // For now, let's check if there are any messages to get the admin ID
    return null; // We'll need to handle this case separately
  } else if (
    userType === USER_TYPES.ADMIN ||
    userType === USER_TYPES.SUPER_ADMIN
  ) {
    // Current user is an admin/super admin looking at a homeowner's conversation
    // The other participant is the homeowner
    return conversation.userId;
  } else {
    // This might be a homeowner looking at another homeowner's conversation (shouldn't happen)
    // or some other edge case
    return conversation.userId;
  }
};
